import React from "react";
import type { AspectId } from "./frame";
import { getFrame } from "./frame";
import type { Theme } from "./theme";
import { FilmPlayer } from "./FilmPlayer";
import { Chat } from "./Chat";
import { buildFilmTimeline, primaryIndexAt, seekTimeFor } from "./transitions";
import {
  segmentScript,
  buildStoryboard,
  compileBeat,
  narrationDuration,
  toCaptions,
  type FormatId,
  type Storyboard as SB,
  type Beat,
} from "./director";
import { requestStoryboard, saveProject, listProjects, getProject, deleteProject, sourceRealImage, youtubeStatus, youtubeAuthUrl, youtubeToken, uploadToYouTube, type SavedProject, type YouTubeStatus } from "./api";
import { exportFilmWebM, isExportSupported, downloadBlob } from "./export";
import type { BrandKit } from "./brand";
import { buildCast } from "./story";
import { applyBeatEdit } from "./beatEdit";
import { planSfx, scheduleSfx } from "./sfx";
import { speakV4, cancelV4 } from "./voice";
import { fetchTTS, prepareVoiceover, releaseVoiceover, VOICES, LANGS, type BeatAudio } from "./tts";
import { pickAsset } from "./assetsClient";

/**
 * Anivaze v4 — Storyboard editor (topic/script → real video).
 *
 * Auto (AI): a topic → the worker Director drafts beats → you edit → it plays as
 * one film → export a .webm. Manual: paste a script instead. The AI only proposes
 * SEMANTIC beats; timing/captions/layout are deterministic, and every beat is
 * editable before export — usable, not a gimmick (PLAN §20–21).
 */

const SAMPLE = `Your brain runs on glucose, and sugar is its fastest source.

Every time you eat it, dopamine spikes and your brain files it as a reward.

So it learns to crave the next hit, long before you feel hungry.

The fix isn't willpower — it's steady fuel so the spikes never start.

Follow for more on how your body actually works.`;

const FORMATS: { id: FormatId; label: string }[] = [
  { id: "explainer", label: "Explainer" },
  { id: "quote", label: "Quote" },
  { id: "story", label: "Story" },
];
const DURATIONS = [20, 30, 45, 60, 120, 180, 300, 600];
const durLabel = (s: number) => (s < 120 ? `${s}s` : `${s / 60}m`);

type Mode = "auto" | "manual" | "chat";
type Source = "ai" | "fallback" | "offline" | "manual" | null;

/** User-facing names for beat roles — spec tokens ("cta", "body") stay internal. */
const ROLE_LABEL: Record<string, string> = {
  hook: "Hook", body: "Point", quote: "Quote", recap: "Recap", cta: "Call to action",
};

type VoState = "none" | "loading" | "ready" | "partial" | "failed";

export const Storyboard: React.FC<{ theme: Theme; aspect: AspectId; guides: boolean }> = ({ theme, aspect, guides }) => {
  const [mode, setMode] = React.useState<Mode>("auto");
  const [topic, setTopic] = React.useState(""); // ask first — no pre-filled topic
  const [script, setScript] = React.useState(SAMPLE);
  const [format, setFormat] = React.useState<FormatId>("explainer");
  const [seconds, setSeconds] = React.useState(30);

  const [sb, setSb] = React.useState<SB>(() => segmentScript(SAMPLE, "explainer"));
  const [sel, setSel] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [t, setT] = React.useState(0);

  const [loading, setLoading] = React.useState(false);
  const [source, setSource] = React.useState<Source>(null);
  const [err, setErr] = React.useState("");
  const [exporting, setExporting] = React.useState(false);
  const [exportPct, setExportPct] = React.useState(0);
  const [voiceOn, setVoiceOn] = React.useState(true);
  const [narrator, setNarrator] = React.useState("asteria"); // Aura speaker id
  const narratorRef = React.useRef(narrator);
  narratorRef.current = narrator;
  const [lang, setLang] = React.useState("en"); // narration language (ANIMATION-STACK §3.5)
  const langRef = React.useRef(lang);
  langRef.current = lang;
  const [artStyle, setArtStyle] = React.useState("default"); // image art style (§3.2)
  const artStyleRef = React.useRef(artStyle);
  artStyleRef.current = artStyle;
  const [sfxOn, setSfxOn] = React.useState(true); // synthesized SFX layer (§3.1)
  const sfxOnRef = React.useRef(sfxOn);
  sfxOnRef.current = sfxOn;
  const sfxCtxRef = React.useRef<AudioContext | null>(null);
  const [brand, setBrand] = React.useState<BrandKit>({});
  const [brandOpen, setBrandOpen] = React.useState(false);
  const [storyMode, setStoryMode] = React.useState(false);
  const [storybookMode, setStorybookMode] = React.useState(false);
  const [overlayMode, setOverlayMode] = React.useState(false);
  const [editCmd, setEditCmd] = React.useState("");
  const [editNote, setEditNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [saved, setSaved] = React.useState<SavedProject[]>([]);
  const [savedOpen, setSavedOpen] = React.useState(false);
  const [saveNote, setSaveNote] = React.useState("");
  const [yt, setYt] = React.useState<YouTubeStatus | null>(null);
  const [publishNote, setPublishNote] = React.useState("");
  const [publishing, setPublishing] = React.useState(false);
  const cast = React.useMemo(() => buildCast(topic || "story"), [topic]);
  // Real voiceover clips (Workers AI TTS) keyed by beat id.
  const [voa, setVoa] = React.useState<Map<string, BeatAudio> | null>(null);
  const [voState, setVoState] = React.useState<VoState>("none");

  // Overlay (green-screen) mode uses HARD cuts (overlap 0) so the chroma green
  // never dims during a crossfade — keyers want constant green.
  const timeline = React.useMemo(() => buildFilmTimeline(sb, overlayMode ? 0 : undefined), [sb, overlayMode]);
  const total = timeline.total;
  const frame = getFrame(aspect);
  const lastSpoken = React.useRef(-1);

  // Fullscreen watch-mode for the Stage.
  const stageHostRef = React.useRef<HTMLDivElement>(null);
  const [isFs, setIsFs] = React.useState(false);
  React.useEffect(() => {
    const onFs = () => setIsFs(document.fullscreenElement === stageHostRef.current);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) { void document.exitFullscreen(); return; }
    if (isFs) { setIsFs(false); return; }
    if (stageHostRef.current?.requestFullscreen) {
      void stageHostRef.current.requestFullscreen();
    } else {
      // iOS Safari has no Element.requestFullscreen on divs — use the CSS
      // fixed-overlay pseudo-fullscreen (ss.fsHost) so the button still works.
      setIsFs(true);
    }
    setPlaying(true);
  };
  // Esc leaves the CSS pseudo-fullscreen too (native FS already handles Esc).
  React.useEffect(() => {
    if (!isFs) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.fullscreenElement) setIsFs(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFs]);

  // Refs so the animation clock can reach live state without re-subscribing.
  const tRef = React.useRef(0);
  React.useEffect(() => { tRef.current = t; }, [t]);
  const voaRef = React.useRef<Map<string, BeatAudio> | null>(null);
  voaRef.current = voa;
  const timelineRef = React.useRef(timeline);
  timelineRef.current = timeline;
  const activeIdxRef = React.useRef(0);
  const audioElRef = React.useRef<HTMLAudioElement | null>(null);
  React.useEffect(() => {
    const el = new Audio();
    audioElRef.current = el;
    return () => {
      el.pause();
      cancelV4();
      releaseVoiceover(voaRef.current);
    };
  }, []);

  /** Keep the narration clip locked to the film clock (handles scrubbing). */
  const syncAudioClock = (now: number) => {
    const el = audioElRef.current;
    if (!el || el.paused || !el.duration) return;
    const w = timelineRef.current.windows[activeIdxRef.current];
    if (!w) return;
    const local = now - w.start;
    if (local >= 0 && Math.abs(el.currentTime - local) > 0.6) {
      try { el.currentTime = Math.max(0, Math.min(local, el.duration - 0.05)); } catch { /* seeking not ready */ }
    }
  };

  // SFX plan is pure over the timeline — the exporter schedules the SAME events.
  const sfxEvents = React.useMemo(() => planSfx(timeline, theme.id), [timeline, theme.id]);
  const sfxEventsRef = React.useRef(sfxEvents);
  sfxEventsRef.current = sfxEvents;

  React.useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const prev = tRef.current;
      const nx = prev + dt >= total ? 0 : prev + dt;
      tRef.current = nx;
      setT(nx);
      syncAudioClock(nx);
      // Fire any SFX whose time was crossed this frame (ignore the loop wrap).
      if (sfxOnRef.current && nx > prev) {
        try {
          const ctx = (sfxCtxRef.current ??= new AudioContext());
          for (const ev of sfxEventsRef.current) {
            if (ev.at > prev && ev.at <= nx) scheduleSfx(ctx, ctx.destination, ev.type, ctx.currentTime);
          }
        } catch { /* SFX must never break playback */ }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, total]);

  const activeIndex = playing ? primaryIndexAt(timeline, t) : Math.min(sel, sb.beats.length - 1);
  activeIdxRef.current = activeIndex;

  // Voiceover — real TTS clip when we have a fresh one; browser speech otherwise
  // (and self-heal by fetching a fresh clip for edited beats in the background).
  const refetchInFlight = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const el = audioElRef.current;
    if (!playing || !voiceOn) {
      cancelV4();
      el?.pause();
      lastSpoken.current = -1;
      return;
    }
    if (activeIndex === lastSpoken.current) return;
    lastSpoken.current = activeIndex;
    const beat = sb.beats[activeIndex];
    if (!beat) return;
    const entry = voa?.get(beat.id);
    if (el && entry && entry.text === beat.narration) {
      cancelV4();
      if (el.src !== entry.url) el.src = entry.url;
      try { el.currentTime = 0; } catch { /* not seekable yet */ }
      void el.play().catch(() => speakV4(beat.narration));
    } else {
      el?.pause();
      speakV4(beat.narration ?? "");
      // Self-heal: clip missing or narration edited → fetch a fresh clip.
      if (voState === "ready" || voState === "partial") {
        const key = `${beat.id}::${beat.narration}`;
        if (!refetchInFlight.current.has(key)) {
          refetchInFlight.current.add(key);
          fetchTTS(beat.narration || " ", undefined, narratorRef.current, langRef.current)
            .then((a) => setVoa((m) => {
              const n = new Map(m ?? []);
              const old = n.get(beat.id);
              if (old) URL.revokeObjectURL(old.url);
              n.set(beat.id, a);
              return n;
            }))
            .catch(() => { /* keep browser voice for this beat */ })
            .finally(() => refetchInFlight.current.delete(key));
        }
      }
    }
  }, [playing, voiceOn, activeIndex, sb, voa, voState]);

  /** Fetch real narration for every beat, then retime beats to the ACTUAL clip
   *  lengths — this is what makes playback one continuous film. */
  const prepareVoice = async (next: SB) => {
    releaseVoiceover(voaRef.current);
    setVoa(null);
    setVoState("loading");
    try {
      const map = await prepareVoiceover(next.beats.map((b) => ({ id: b.id, narration: b.narration })), narratorRef.current, langRef.current);
      setSb((s) => ({
        ...s,
        beats: s.beats.map((b) => {
          const a = map.get(b.id);
          if (!a || a.text !== b.narration) {
            // No clip → browser speech narrates. It runs SLOWER than the 2.6wps
            // estimate, which used to cut narration off 1–3s early — give it a
            // slower-rate budget so the beat outlasts the speech.
            const wordCount = (b.narration || "").trim().split(/\s+/).filter(Boolean).length;
            const dur = Math.max(2.2, wordCount / 2.0 + 1.4);
            return { ...b, dur, captions: toCaptions(b.narration || " ", dur) };
          }
          const dur = Math.max(2.2, a.duration + 0.55); // clip + a small breath
          return { ...b, dur, captions: toCaptions(b.narration || " ", dur) };
        }),
      }));
      setVoa(map);
      setVoState(map.size === next.beats.length ? "ready" : "partial");
    } catch {
      setVoState("failed"); // browser speech still narrates
    }
  };

  const applyStoryboard = (next: SB) => {
    setSb(next);
    setSel(0);
    setT(0);
    setPlaying(false);
  };

  /** Fetch generated props/scenes for image & sticker beats (reuse-≥5 enforced
   *  server-side); beats show a pending placeholder until their asset lands.
   *  The art style (ANIMATION-STACK §3.2) is the server-side pack — a style is
   *  a separate cached pool per tag, so switching styles regenerates once then
   *  reuses forever. */
  const prepareAssets = async (next: SB) => {
    const targets = next.beats.filter(
      (b) => (b.visual.kind === "image" || b.visual.kind === "sticker") && !b.visual.assetUri,
    );
    if (targets.length === 0) return;
    await Promise.allSettled(
      targets.map(async (b) => {
        const subject = b.visual.items[0] || b.title;
        const tag = subject.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60) || "prop";
        try {
          const a = await pickAsset(tag, b.visual.kind === "image" ? "scene" : "sticker", subject, artStyleRef.current);
          setSb((s) => ({
            ...s,
            beats: s.beats.map((x) => (x.id === b.id ? { ...x, visual: { ...x.visual, assetUri: a.dataUri } } : x)),
          }));
        } catch { /* placeholder stays; video still plays */ }
      }),
    );
  };

  /** Switching art style re-illustrates every image beat in the new style. */
  const changeArtStyle = (style: string) => {
    setArtStyle(style);
    artStyleRef.current = style;
    const cleared: SB = {
      ...sb,
      beats: sb.beats.map((b) =>
        b.visual.kind === "image" || b.visual.kind === "sticker"
          ? { ...b, visual: { ...b.visual, assetUri: undefined } }
          : b),
    };
    setSb(cleared);
    void prepareAssets(cleared);
  };

  // Storybook sub-mode: every beat needs a full-bleed scene still, so when it's
  // switched on, fetch a generated scene image for each beat that lacks one.
  // (This is the heaviest image mode — one gen per new scene; reuse still applies.)
  React.useEffect(() => {
    if (!storybookMode) return;
    let cancelled = false;
    (async () => {
      for (const b of sb.beats) {
        if (cancelled) break;
        if ((b.visual as { assetUri?: string }).assetUri) continue;
        const subject = (b.title || "scene").slice(0, 90);
        const tag = subject.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60) || "scene";
        try {
          const a = await pickAsset(tag, "scene", subject, artStyleRef.current);
          if (cancelled) break;
          setSb((s) => ({ ...s, beats: s.beats.map((x) => (x.id === b.id ? { ...x, visual: { ...x.visual, assetUri: a.dataUri } } : x)) }));
        } catch { /* placeholder plate stays */ }
      }
    })();
    return () => { cancelled = true; };
  }, [storybookMode, sb.beats.length]);

  const generateManual = () => {
    setSource("manual");
    setErr("");
    const next = segmentScript(script, format);
    applyStoryboard(next);
    void prepareVoice(next);
    void prepareAssets(next);
  };

  const generateAuto = async (topicOverride?: string) => {
    // onClick handlers pass an event — only honor a real string override (from chat).
    const override = typeof topicOverride === "string" ? topicOverride.trim() : "";
    const useTopic = (override || topic).trim();
    if (override && override !== topic) setTopic(override);
    if (!useTopic) return;
    setLoading(true);
    setErr("");
    setSource(null);
    try {
      let resp = await requestStoryboard({ topic: useTopic, format, targetSeconds: seconds, language: lang });
      if (resp.source === "fallback") {
        // A transient AI blip must not ship template beats — retry once, automatically.
        try {
          const again = await requestStoryboard({ topic: useTopic, format, targetSeconds: seconds, language: lang });
          if (again.source === "ai") resp = again;
        } catch { /* keep the first response */ }
      }
      const next = buildStoryboard(resp.beats, format, useTopic);
      applyStoryboard(next);
      setSource(resp.source);
      void prepareVoice(next);
      void prepareAssets(next);
    } catch (err) {
      // Guardrail block: show the boundary message and do NOT generate anything.
      if ((err as { blocked?: boolean })?.blocked) {
        setErr((err as Error).message);
        setLoading(false);
        return;
      }
      // Worker offline → deterministic local fallback so it still works.
      const next = segmentScript(useTopic, format, useTopic);
      applyStoryboard(next);
      setSource("offline");
      void prepareVoice(next);
    } finally {
      setLoading(false);
    }
  };

  const selectBeat = (i: number) => {
    setSel(i);
    setPlaying(false);
    setT(seekTimeFor(timeline, i));
  };

  const patchBeat = (i: number, patch: Partial<Beat>) =>
    setSb((s) => ({ ...s, beats: s.beats.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));

  const editNarration = (i: number, text: string) => {
    const dur = narrationDuration(text || " ");
    patchBeat(i, { narration: text, captions: toCaptions(text || " ", dur), dur });
  };

  // Conversational editing (PRODUCT-CHAT-EDIT §4): parse a plain instruction for
  // the selected beat and mutate it locally (~$0) — change the image, switch the
  // visual, add a symbol, rename. "reword" is flagged as needing regeneration.
  const applyEdit = async () => {
    if (!editCmd.trim() || applying) return;
    // "real photo of X" pulls a licensed Wikimedia image (async) with a credit line.
    const photoM = editCmd.match(/(?:find|use|get|add|show|put)\s+(?:me\s+)?(?:a\s+)?(?:real\s+|actual\s+)?photo(?:graph)?\s+(?:of\s+)?(.+)/i)
      || editCmd.match(/real\s+(?:image|picture|pic)\s+(?:of\s+)?(.+)/i);
    if (photoM && photoM[1].trim()) {
      const subject = photoM[1].trim().replace(/[."']+$/, "");
      setApplying(true);
      setEditNote(`Finding a real photo of “${subject}”…`);
      try {
        const img = await sourceRealImage(subject);
        const credit = [img.attribution, img.license].filter(Boolean).join(" · ");
        const nextBeats = sb.beats.map((b, i) => (i === sel ? { ...b, visual: { ...b.visual, kind: "image" as const, assetUri: img.imageUrl, credit } } : b));
        setSb((s) => ({ ...s, beats: nextBeats }));
        setEditNote(`Photo added — ${credit} (Wikimedia)`);
        setEditCmd("");
      } catch {
        setEditNote("Couldn't find a licensed photo for that subject — try a more famous name, or upload your own with 🖼 Image.");
      } finally {
        setApplying(false);
      }
      return;
    }
    const res = applyBeatEdit(selBeat, editCmd);
    setEditNote(res.note);
    if (res.changed) {
      const nextBeats = sb.beats.map((b, i) => (i === sel ? res.beat : b));
      setSb((s) => ({ ...s, beats: nextBeats }));
      if (res.fetchAsset) void prepareAssets({ ...sb, beats: nextBeats });
      setEditCmd("");
    } else if (res.needsRegen) {
      // A reword re-plans just this beat — regenerate keeps timing/captions synced.
      editNarration(sel, selBeat.narration); // re-derive captions/timing deterministically
    }
  };

  const uploadBeatImage = (file: File) => {
    const rd = new FileReader();
    rd.onload = () => {
      const uri = String(rd.result);
      setSb((s) => ({ ...s, beats: s.beats.map((b, i) => (i === sel ? { ...b, visual: { ...b.visual, kind: "image", assetUri: uri } } : b)) }));
      setEditNote("Your image is placed in this beat.");
    };
    rd.readAsDataURL(file);
  };

  // Save / reopen (PRODUCT-CHAT-EDIT §7): the spec is stored (30d → transcript 90d).
  const refreshSaved = async () => { try { setSaved(await listProjects()); } catch { /* offline */ } };
  const doSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveNote("Saving…");
    try { const r = await saveProject(sb, (sb as any).topic || topic); setSaveNote(`Saved · kept ${r.daysLeft} days`); void refreshSaved(); }
    catch { setSaveNote("Couldn't save — you may be offline. Your video is still here; try 💾 Save again in a moment."); }
    finally { setSaving(false); }
  };
  const reopen = async (id: string) => {
    const spec = await getProject(id);
    if (spec && Array.isArray(spec.beats)) { applyStoryboard(spec); setSavedOpen(false); }
  };
  React.useEffect(() => { if (savedOpen) void refreshSaved(); }, [savedOpen]);

  // YouTube publish (PRODUCT-CHAT-EDIT §8). Scaffolding — activates once the worker
  // has the Google secrets; degrades to a clear message otherwise.
  React.useEffect(() => {
    void youtubeStatus().then(setYt);
    if (typeof window !== "undefined" && window.location.search.includes("youtube=linked")) setPublishNote("YouTube account linked ✓ — hit Publish again.");
  }, []);
  const publishYouTube = async () => {
    const status = await youtubeStatus();
    setYt(status);
    if (!status.configured) { setPublishNote("YouTube publishing isn't available here yet — you can still ⬇ Export the video and upload it yourself."); return; }
    if (!status.linked) {
      const url = await youtubeAuthUrl(typeof window !== "undefined" ? window.location.href.split("?")[0] : "");
      if (url && typeof window !== "undefined") window.location.href = url;
      else setPublishNote("Couldn't start YouTube linking.");
      return;
    }
    if (!exportOk) { setPublishNote("Rendering needs Chrome / Edge."); return; }
    setPublishing(true);
    setPublishNote("Rendering the video…");
    let stage: "render" | "upload" = "render";
    try {
      const blob = await exportFilmWebM(sb, theme, aspect, { voiceover: voiceOn ? voa : null, container: "mp4", speed: 0.92, brand, story: storyMode, cast, storybook: storybookMode, overlay: overlayMode, sfx: sfxOn });
      stage = "upload";
      setPublishNote("Uploading to YouTube…");
      const token = await youtubeToken();
      const title = (((sb as any).topic || topic || "Anivaze video") as string).slice(0, 90);
      const description = sb.beats.map((b) => b.narration).filter(Boolean).join("\n\n").slice(0, 4000) + "\n\nMade with Anivaze.";
      const { url } = await uploadToYouTube(token, blob, { title, description, privacy: "unlisted" });
      setPublishNote(`Published (unlisted) → ${url}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPublishNote(stage === "upload"
        ? `Couldn't upload to YouTube: ${msg} — if this mentions a token, re-link your YouTube account.`
        : `Couldn't render the video: ${msg} — try ⬇ Export to check rendering.`);
    } finally {
      setPublishing(false);
    }
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= sb.beats.length) return;
    setSb((s) => {
      const beats = [...s.beats];
      [beats[i], beats[j]] = [beats[j], beats[i]];
      return { ...s, beats };
    });
    setSel(j);
  };

  const remove = (i: number) => {
    if (sb.beats.length <= 1) return;
    setSb((s) => ({ ...s, beats: s.beats.filter((_, j) => j !== i) }));
    setSel((x) => Math.max(0, Math.min(x, sb.beats.length - 2)));
    setPlaying(false);
  };

  const addAfter = (i: number) => {
    const nb: Beat = { ...compileBeat(0, "body", "New point", "New point to cover.", format), id: `beat-new-${Date.now()}` };
    setSb((s) => {
      const beats = [...s.beats];
      beats.splice(i + 1, 0, nb);
      return { ...s, beats };
    });
    setSel(i + 1);
    setPlaying(false);
  };

  const doExport = async () => {
    setExporting(true);
    setExportPct(0);
    setErr("");
    setPlaying(false);
    try {
      const blob = await exportFilmWebM(sb, theme, aspect, {
        onProgress: setExportPct,
        voiceover: voiceOn ? voa : null,
        container: "mp4",   // prefer H.264/AAC (universal); falls back to WebM automatically
        speed: 0.92,        // pacing ramp: relax the slightly-fast feel on export
        brand,              // bake the brand kit (header/footer/watermark/logo) into the export
        story: storyMode,   // Storyline mode: render each beat as the cast on a stage
        cast,
        storybook: storybookMode, // Storybook: full-bleed generated scene stills
        overlay: overlayMode,     // Green-screen: chroma green + captions + voice only
        sfx: sfxOn,               // synthesized whoosh/pop/scratch layer
      });
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      downloadBlob(blob, `anivaze-${sb.format}-${aspect.replace(":", "x")}.${ext}`);
    } catch (e) {
      // Surface the REAL failure + browser, so a bug report tells us exactly what broke.
      const browser = navigator.userAgent.match(/(Edg|Chrome|Firefox|Safari)\/[\d.]+/)?.[0] ?? "unknown browser";
      setErr(`Export failed: ${e instanceof Error ? e.message : String(e)} [${browser}] — please share this exact message.`);
    } finally {
      setExporting(false);
    }
  };

  const selBeat = sb.beats[Math.min(sel, sb.beats.length - 1)];
  const exportOk = isExportSupported();

  return (
    <div style={ss.wrap}>
      {/* Input */}
      <div style={ss.inputCard}>
        <div style={ss.modeRow}>
          {(["auto", "manual", "chat"] as Mode[]).map((m) => (
            <button key={m} className="v4-pill" aria-pressed={mode === m} onClick={() => setMode(m)} style={{ ...ss.modeBtn, ...(mode === m ? ss.modeOn : null) }}>
              {m === "auto" ? "Auto (AI)" : m === "manual" ? "Manual script" : "💬 Chat"}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <div style={ss.formatRow}>
            {FORMATS.map((f) => (
              <button key={f.id} className="v4-pill" aria-pressed={format === f.id} onClick={() => setFormat(f.id)} style={{ ...ss.chip, ...(format === f.id ? ss.chipOn : null) }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {mode === "auto" ? (
          <div className="v4-pop-in" style={ss.autoRow}>
            <input
              autoFocus
              className="v4-input v4-field"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What should the video explain? e.g. how do black holes form"
              aria-label="Video topic"
              style={ss.topicInput}
              onKeyDown={(e) => e.key === "Enter" && !loading && topic.trim() && generateAuto()}
            />
            <select className="v4-input" value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} aria-label="Video length" title="Target video length" style={ss.select}>
              {DURATIONS.map((d) => (
                <option key={d} value={d}>{durLabel(d)}</option>
              ))}
            </select>
            <select className="v4-input" value={lang} onChange={(e) => { setLang(e.target.value); langRef.current = e.target.value; }} aria-label="Narration language" title="Script & captions are written in this language. Studio voiceover is English for now — other languages preview with your device's voice." style={ss.select}>
              {LANGS.map((l) => (
                <option key={l.id} value={l.id}>{l.label}</option>
              ))}
            </select>
            <button className="v4-btn-primary" onClick={() => generateAuto()} disabled={loading || !topic.trim()} style={ss.generate}>
              {loading ? "Directing…" : "✦ Generate with AI"}
            </button>
          </div>
        ) : mode === "chat" ? (
          <div className="v4-pop-in" style={{ width: "min(100%, 560px)", height: 420 }}>
            <Chat theme={theme} onMakeVideo={(t) => { setMode("auto"); void generateAuto(t); }} />
          </div>
        ) : (
          <div className="v4-pop-in" style={ss.manualRow}>
            <textarea className="v4-input v4-field" value={script} onChange={(e) => setScript(e.target.value)} aria-label="Your script" style={ss.textarea} spellCheck={false} />
            <button className="v4-btn-primary" onClick={generateManual} style={ss.generate}>✦ Build video from script</button>
          </div>
        )}

        <div role="status" aria-live="polite" style={ss.statusRow}>
          {source === null && (
            <>
              <span style={{ ...ss.badge, color: "#e8b34a", borderColor: "#7a6a33" }}>Sample</span>
              <span style={{ ...ss.hint, color: "#e8b34a" }}>
                {mode === "auto" ? "Type a topic above to make your own"
                  : mode === "manual" ? "Edit the script, then press ✦ Build"
                  : "Ask in chat, then press 🎬 Make a video"}
              </span>
            </>
          )}
          {source === "manual" && <span style={{ ...ss.badge, color: "#37d39a", borderColor: "#2a5" }}>Your script</span>}
          {source === "ai" && <span style={{ ...ss.badge, color: "#37d39a", borderColor: "#2a5" }}>AI Director</span>}
          {source === "offline" && <span style={ss.badge}>Offline — built on device</span>}
          {voState === "loading" && <span style={ss.badge}>preparing voiceover…</span>}
          {voState === "ready" && <span style={{ ...ss.badge, color: "#37d39a", borderColor: "#2a5" }}>voiceover ready</span>}
          {voState === "partial" && <span style={ss.badge}>voiceover ready · a device voice fills a few beats</span>}
          {voState === "failed" && <span style={ss.badge}>voice server busy · using your device's voice for now</span>}
          <span style={ss.hint}>
            {sb.beats.length} beats · {total.toFixed(1)}s — a beat is one scene; click any beat below to edit it
          </span>
        </div>
        {source === "fallback" && (
          <div role="alert" className="v4-pop-in" style={ss.warnBanner}>
            <b>The AI Director couldn't be reached</b> — this is a basic placeholder outline, not a real
            script. It usually recovers in seconds.
            <button className="v4-btn-primary" onClick={() => generateAuto()} disabled={loading} style={ss.warnRetry}>
              {loading ? "Retrying…" : "↻ Retry with AI"}
            </button>
          </div>
        )}
        {source === "offline" && (
          <div role="alert" className="v4-pop-in" style={ss.warnBanner}>
            <b>Couldn't reach the AI</b> — this outline was built on your device from the topic text, without AI.
            <button className="v4-btn-primary" onClick={() => generateAuto()} disabled={loading} style={ss.warnRetry}>
              {loading ? "Retrying…" : "↻ Retry with AI"}
            </button>
          </div>
        )}
      </div>

      {/* Player + editor */}
      <div style={ss.mid}>
        <div style={ss.stageWrap}>
          <div ref={stageHostRef} style={isFs ? ss.fsHost : ss.stageHost}>
            <div style={{
              ...ss.stageBox, aspectRatio: `${frame.w} / ${frame.h}`,
              ...(isFs
                ? {
                    border: "none", borderRadius: 0, boxShadow: "none",
                    ...(frame.w / frame.h >= 1
                      ? { width: "100vw", height: "auto", maxHeight: "100vh" }
                      : { height: "100vh", width: "auto", maxWidth: "100vw" }),
                  }
                : (frame.w / frame.h >= 1
                    ? { width: "100%", height: "auto", maxHeight: "72vh" }
                    : { height: "min(72vh, 760px)", width: "auto", maxWidth: "100%" })),
            }}>
              <FilmPlayer sb={sb} theme={theme} aspect={aspect} t={playing ? t : seekTimeFor(timeline, activeIndex)} guides={guides} timeline={timeline} brand={brand} story={storyMode} cast={cast} storybook={storybookMode} overlay={overlayMode} />
            </div>
            {isFs && (
              <button className="v4-fs-exit" onClick={toggleFullscreen} aria-label="Exit fullscreen" style={ss.fsExit} title="Exit fullscreen (Esc)">⤢ Exit</button>
            )}
          </div>
          <div style={ss.transport}>
            <button className="v4-btn-primary" aria-label={playing ? "Pause" : "Play"} title={playing ? "Pause" : "Play"} onClick={() => setPlaying((p) => !p)} style={ss.play}>{playing ? "❚❚" : "►"}</button>
            <input
              type="range" min={0} max={total} step={0.25} value={playing ? t : seekTimeFor(timeline, activeIndex)}
              onChange={(e) => { setPlaying(true); setT(parseFloat(e.target.value)); }}
              aria-valuetext={`${(playing ? t : seekTimeFor(timeline, activeIndex)).toFixed(0)} of ${total.toFixed(0)} seconds`}
              style={ss.scrub} aria-label="Timeline"
            />
            <span style={ss.time}>{(playing ? t : seekTimeFor(timeline, activeIndex)).toFixed(1)} / {total.toFixed(1)}s</span>
            <button
              className="v4-btn v4-toggle"
              aria-pressed={voiceOn}
              onClick={() => setVoiceOn((v) => !v)}
              style={ss.miniBtn}
              aria-label={voiceOn ? "Mute voiceover" : "Unmute voiceover"}
              title={voiceOn ? "Mute voiceover" : "Unmute voiceover"}
            >
              {voiceOn ? "🔊" : "🔇"}
            </button>
            <button
              className="v4-btn v4-toggle"
              aria-pressed={sfxOn}
              onClick={() => setSfxOn((v) => !v)}
              style={ss.miniBtn}
              aria-label={sfxOn ? "Turn sound effects off" : "Turn sound effects on"}
              title={sfxOn ? "Sound effects on (whooshes & pops)" : "Sound effects off"}
            >
              ✨🔉
            </button>
            <button className="v4-btn" onClick={toggleFullscreen} aria-label="Watch fullscreen" style={ss.miniBtn} title="Watch fullscreen">⤢</button>
            {lang === "en" && (
              <select
                className="v4-input"
                value={narrator}
                aria-label="Narrator voice"
                title="Narrator voice — changing it re-records the narration (free)"
                onChange={(e) => { setNarrator(e.target.value); narratorRef.current = e.target.value; void prepareVoice(sb); }}
                style={{ ...ss.select, maxWidth: 168, flex: "0 1 auto" }}
              >
                {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            )}
          </div>
          <div style={ss.exportRow}>
            <div style={ss.ctlGroup}>
              <div style={ss.rowLabel}>Style</div>
              <div style={ss.ctlRow}>
                <button className="v4-btn v4-toggle" aria-pressed={storyMode} onClick={() => { setStoryMode((v) => !v); setStorybookMode(false); setOverlayMode(false); }} style={ss.miniBtn} title="Act it out: a character performs each beat on a stage">🎭 Act it out</button>
                <button className="v4-btn v4-toggle" aria-pressed={storybookMode} onClick={() => { setStorybookMode((v) => !v); setStoryMode(false); setOverlayMode(false); }} style={ss.miniBtn} title="Storybook: an illustrated still per scene, with subtitles">📖 Storybook</button>
                <button className="v4-btn v4-toggle" aria-pressed={overlayMode} onClick={() => { setOverlayMode((v) => !v); setStoryMode(false); setStorybookMode(false); }} style={ss.miniBtn} title="Green-screen overlay: voiceover + subtitles on chroma green — export and key it over any footage in any editor">🟩 Overlay</button>
                <button className="v4-btn" aria-expanded={brandOpen} onClick={() => setBrandOpen((v) => !v)} style={ss.miniBtn} title="Brand kit: header / footer / watermark / logo">🎨 Brand</button>
                <select
                  className="v4-input"
                  value={artStyle}
                  aria-label="Art style for generated images"
                  title="Art style — every illustrated image in the video is generated in this style"
                  onChange={(e) => changeArtStyle(e.target.value)}
                  style={{ ...ss.select, maxWidth: 150, flex: "0 1 auto" }}
                >
                  <option value="default">🖼 Flat vector</option>
                  <option value="watercolor">🖼 Watercolor</option>
                  <option value="anime">🖼 Anime</option>
                  <option value="oil">🖼 Oil paint</option>
                  <option value="3d">🖼 3D render</option>
                  <option value="pixel">🖼 Pixel art</option>
                  <option value="ink">🖼 Ink line</option>
                </select>
              </div>
            </div>
            <div style={ss.ctlGroup}>
              <div style={ss.rowLabel}>Export</div>
              <div style={ss.ctlRow}>
                <button className="v4-btn" onClick={doExport} disabled={!exportOk || exporting} style={ss.exportBtn} title={exportOk ? "Render and download the video (MP4, or WebM where MP4 isn't supported)" : "Export needs Chrome or Edge"}>
                  {exporting ? `Rendering ${Math.round(exportPct * 100)}%` : "⬇ Export video"}
                </button>
                {exporting && (
                  <div role="progressbar" aria-label="Export progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(exportPct * 100)} style={ss.progressTrack}>
                    <div style={{ ...ss.progressFill, transform: `scaleX(${exportPct})` }} />
                  </div>
                )}
                <button className="v4-btn" onClick={publishYouTube} disabled={publishing} style={ss.miniBtn} title={yt?.configured ? (yt?.linked ? `Publish (unlisted) to ${yt.channel || "YouTube"}` : "Link your YouTube account") : "YouTube publishing isn't available here yet — export and upload yourself"}>
                  {publishing ? "Publishing…" : yt?.linked ? "📤 Publish to YouTube" : yt?.configured ? "🔗 Link YouTube" : "📤 YouTube"}
                </button>
              </div>
              {!exportOk && <span style={ss.hint}>Export works in Chrome / Edge.</span>}
              <span style={ss.hint}>
                {!voiceOn ? "Voiceover is muted — unmute 🔊 to export with narration."
                  : voState === "loading" ? "Preparing the voiceover — it's included in the export once ready."
                  : voState === "ready" ? "Export includes the voiceover."
                  : voState === "partial" ? "Export includes the voiceover (a device voice fills a few beats)."
                  : voState === "failed" ? "Voice server is busy — an export made now will have no narration. 💾 Save and retry in a minute."
                  : "The voiceover is included once it's ready."}
              </span>
            </div>
            <div style={ss.ctlGroup}>
              <div style={ss.rowLabel}>Library</div>
              <div style={ss.ctlRow}>
                <button className="v4-btn" onClick={doSave} disabled={saving} style={ss.miniBtn} title="Save this video to reopen later (kept 30 days)">{saving ? "Saving…" : "💾 Save"}</button>
                <button className="v4-btn" aria-expanded={savedOpen} onClick={() => setSavedOpen((v) => !v)} style={ss.miniBtn} title="Your saved videos">📁 My videos</button>
              </div>
            </div>
            {(saveNote || publishNote) && (
              <div role="status" aria-live="polite" style={ss.statusLine}>{publishNote || saveNote}</div>
            )}
          </div>
          {brandOpen && (
            <div className="v4-pop-in" style={ss.brandPanel}>
              <input className="v4-input v4-field" placeholder="Header text" aria-label="Header text" value={brand.headerText ?? ""} onChange={(e) => setBrand({ ...brand, headerText: e.target.value || undefined })} style={ss.input} />
              <input className="v4-input v4-field" placeholder="Footer text" aria-label="Footer text" value={brand.footerText ?? ""} onChange={(e) => setBrand({ ...brand, footerText: e.target.value || undefined })} style={ss.input} />
              <input className="v4-input v4-field" placeholder="Watermark text" aria-label="Watermark text" value={brand.watermarkText ?? ""} onChange={(e) => setBrand({ ...brand, watermarkText: e.target.value || undefined })} style={ss.input} />
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <label style={ss.hint}>Accent <input type="color" value={brand.accent ?? theme.palette.primary} onChange={(e) => setBrand({ ...brand, accent: e.target.value })} aria-label="Brand accent colour" /></label>
                <label style={ss.hint}>Logo <input type="file" accept="image/*" aria-label="Upload logo" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const rd = new FileReader(); rd.onload = () => setBrand((b) => ({ ...b, logoHref: String(rd.result) })); rd.readAsDataURL(f); }} /></label>
                {brand.logoHref && <button className="v4-btn" onClick={() => setBrand({ ...brand, logoHref: undefined })} style={ss.miniBtn}>Remove logo</button>}
              </div>
            </div>
          )}
          {savedOpen && (
            <div className="v4-pop-in" style={ss.brandPanel}>
              <div style={ss.rowLabel}>Your videos · kept 30 days</div>
              {saved.length === 0 && <span style={ss.hint}>No saved videos yet — hit 💾 Save.</span>}
              {saved.map((p, i) => (
                <div key={p.projectId} className="v4-pop-in" style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between", animationDelay: `${Math.min(i, 8) * 30}ms` }}>
                  <button className="v4-btn" onClick={() => reopen(p.projectId)} style={{ ...ss.miniBtn, flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title="Reopen this video">
                    {p.kind === "transcript" ? "📄 " : "🎬 "}{p.title || "Untitled"}
                  </button>
                  <span style={ss.hint} title="Saved videos are deleted after this many days">{p.daysLeft} days left</span>
                  <button className="v4-btn v4-btn-danger" aria-label={`Delete ${p.title || "Untitled"}`} onClick={async (e) => { const b = e.currentTarget; b.disabled = true; try { await deleteProject(p.projectId); void refreshSaved(); } finally { b.disabled = false; } }} style={{ ...ss.miniBtn, color: "#ff7a7a" }} title="Delete">🗑</button>
                </div>
              ))}
            </div>
          )}
          {err && <div role="alert" className="v4-pop-in" style={ss.err}>{err}</div>}
        </div>

        {/* Selected beat editor */}
        <div style={ss.editor}>
          <div style={ss.rowLabel}>Beat {sel + 1} · <span style={{ color: "#ff8a66" }}>{ROLE_LABEL[selBeat.role] ?? selBeat.role}</span></div>
          <label htmlFor="beat-title" style={ss.fieldLabel}>On-screen title</label>
          <input id="beat-title" className="v4-input" value={selBeat.title} onChange={(e) => patchBeat(sel, { title: e.target.value })} style={ss.input} />
          <label htmlFor="beat-narration" style={ss.fieldLabel}>Narration / caption</label>
          <textarea id="beat-narration" className="v4-input" value={selBeat.narration} onChange={(e) => editNarration(sel, e.target.value)} style={ss.smallArea} spellCheck={false} />
          <div style={ss.editBtns}>
            <button className="v4-btn" aria-label="Move beat earlier" onClick={() => move(sel, -1)} style={ss.miniBtn} title="Move earlier">←</button>
            <button className="v4-btn" aria-label="Move beat later" onClick={() => move(sel, 1)} style={ss.miniBtn} title="Move later">→</button>
            <button className="v4-btn" onClick={() => addAfter(sel)} style={ss.miniBtn} title="Add beat after">＋ Beat</button>
            <button className="v4-btn v4-btn-danger" aria-label="Delete beat" onClick={() => remove(sel)} style={{ ...ss.miniBtn, color: "#ff7a7a" }} title="Delete beat">🗑</button>
          </div>
          <label htmlFor="beat-edit" style={ss.fieldLabel}>Ask for a change</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              id="beat-edit"
              className="v4-input v4-field"
              value={editCmd}
              onChange={(e) => setEditCmd(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !applying) applyEdit(); }}
              placeholder="e.g. change the image to a volcano · make it a chart · add solar panel"
              style={{ ...ss.input, flex: "1 1 240px" }}
            />
            <button className="v4-btn" onClick={applyEdit} disabled={applying} style={ss.miniBtn} title="Apply this change to the beat">{applying ? "Applying…" : "Apply"}</button>
            <label className="v4-btn" style={{ ...ss.miniBtn, cursor: "pointer", position: "relative" }} title="Place your own image in this beat">🖼 Image
              <input
                type="file" accept="image/*" aria-label="Upload an image for this beat"
                style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBeatImage(f); }}
              />
            </label>
          </div>
          {editNote && <div role="status" aria-live="polite" style={ss.hint}>{editNote}</div>}
        </div>
      </div>

      {/* Beat strip */}
      <div style={ss.stripWrap}>
        {sb.beats.map((b, i) => (
          <button
            key={b.id}
            className="v4-card beat-in"
            aria-current={i === activeIndex ? "true" : undefined}
            aria-label={`Beat ${i + 1}: ${b.title}, ${b.dur.toFixed(1)} seconds`}
            onClick={() => selectBeat(i)}
            style={{ ...ss.beatCard, ...(i === activeIndex ? ss.beatCardOn : null), animationDelay: `${Math.min(i, 12) * 40}ms` }}
          >
            <span style={ss.beatRole}>{i + 1} · {ROLE_LABEL[b.role] ?? b.role}</span>
            <span style={ss.beatTitle}>{b.title}</span>
            <span style={ss.beatDur}>{b.dur.toFixed(1)}s</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/*
 * Chrome tokens. Type scale (3 steps, ~1.13): 11px micro labels · 12.5px meta ·
 * 14px controls (inputs 16px so iOS Safari never auto-zooms). Neutral ramp:
 * #0c0e11 root · #111418 card · #0f1216 well · #171b20 raised. Accent #ff5d33
 * carries primary actions + selection; state colors are single pairs (danger
 * #ff7a7a · success #37d39a/#2a5 · warning #e8b34a/#7a6a33). 4px spacing grid.
 */
const ss: Record<string, React.CSSProperties> = {
  wrap: { display: "flex", flexDirection: "column", gap: 16, width: "100%", minWidth: 0 },
  inputCard: { display: "flex", flexDirection: "column", gap: 12, background: "#111418", border: "1px solid #1e232a", borderRadius: 14, padding: 16 },
  modeRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  modeBtn: { background: "transparent", color: "#9aa4b0", border: "1px solid #262c34", borderRadius: 999, padding: "6px 14px", cursor: "pointer", fontSize: 14, minHeight: 40 },
  modeOn: { background: "#ff5d33", color: "#12151a", borderColor: "#ff5d33", fontWeight: 600 },
  formatRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  // The format picker shares the pill grammar (this pair was previously missing —
  // the buttons rendered as unstyled UA chrome).
  chip: { background: "transparent", color: "#9aa4b0", border: "1px solid #262c34", borderRadius: 999, padding: "6px 14px", cursor: "pointer", fontSize: 14, minHeight: 40 },
  chipOn: { background: "#171b20", color: "#fff", borderColor: "#3a424c", fontWeight: 600 },
  autoRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  topicInput: { flex: "1 1 260px", minWidth: 0, background: "#0f1216", color: "#e6eaef", border: "1px solid #262c34", borderRadius: 10, padding: "10px 12px", fontSize: 16 },
  select: { background: "#0f1216", color: "#e6eaef", border: "1px solid #262c34", borderRadius: 10, padding: "0 10px", fontSize: 14, minHeight: 40 },
  manualRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" },
  textarea: { flex: "1 1 320px", height: 92, resize: "vertical", background: "#0f1216", color: "#dfe4ea", border: "1px solid #262c34", borderRadius: 10, padding: 12, fontSize: 16, lineHeight: 1.5 },
  generate: { background: "#ff5d33", color: "#12151a", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: "pointer", fontSize: 14, whiteSpace: "nowrap", minHeight: 40 },
  statusRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  warnBanner: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#3a1d15", border: "1px solid #b05038", borderRadius: 10, padding: "10px 14px", fontSize: 14, color: "#ffd9cc", lineHeight: 1.45 },
  warnRetry: { background: "#ff5d33", color: "#12151a", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", fontSize: 14, whiteSpace: "nowrap", minHeight: 40 },
  badge: { fontFamily: "monospace", fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "#9aa4b0", border: "1px solid #333b44", borderRadius: 999, padding: "4px 8px" },
  hint: { fontSize: 12.5, color: "#8a939e" },
  rowLabel: { fontFamily: "monospace", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#7c8794" },
  mid: { display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" },
  stageWrap: { flex: "1 1 300px", display: "flex", flexDirection: "column", gap: 12, alignItems: "center", minWidth: 0 },
  stageHost: { width: "100%", display: "flex", justifyContent: "center" },
  stageBox: { background: "#000", borderRadius: 18, overflow: "hidden", border: "1px solid #333b44", boxShadow: "0 24px 60px -30px rgba(0,0,0,0.9)" },
  fsHost: { position: "fixed", inset: 0, zIndex: 9999, background: "#000", display: "flex", alignItems: "center", justifyContent: "center" },
  fsExit: { position: "fixed", top: 16, right: 20, zIndex: 10000, background: "rgba(0,0,0,0.55)", color: "#fff", border: "1px solid #555", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 14, minHeight: 40 },
  transport: { display: "flex", alignItems: "center", gap: 8, width: "100%", maxWidth: 560 },
  play: { width: 44, height: 44, borderRadius: 999, border: "none", background: "#ff5d33", color: "#12151a", cursor: "pointer", flex: "0 0 auto" },
  scrub: { flex: "1 1 auto", accentColor: "#ff5d33", minWidth: 60 },
  time: { fontFamily: "monospace", fontSize: 12.5, color: "#8a939e", fontVariantNumeric: "tabular-nums" },
  exportRow: { display: "flex", flexDirection: "column", gap: 16, width: "100%", maxWidth: 560 },
  ctlGroup: { display: "flex", flexDirection: "column", gap: 6 },
  ctlRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  statusLine: { fontSize: 12.5, color: "#a8b2bd", minHeight: 18, overflowWrap: "anywhere" },
  brandPanel: { display: "flex", flexDirection: "column", gap: 8, width: "100%", maxWidth: 560, padding: 12, background: "#0f1216", border: "1px solid #262c34", borderRadius: 10 },
  exportBtn: { background: "#171b20", color: "#e6eaef", border: "1px solid #333b44", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 14, fontWeight: 600, minHeight: 40 },
  progressTrack: { flex: "1 1 auto", height: 6, background: "#20262e", borderRadius: 999, overflow: "hidden", minWidth: 80 },
  progressFill: { height: "100%", background: "#ff5d33", transformOrigin: "left", transition: "transform 100ms linear" },
  err: { color: "#ff7a7a", fontSize: 12.5, maxWidth: 560, textAlign: "center" },
  editor: { flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 8, background: "#111418", border: "1px solid #1e232a", borderRadius: 14, padding: 16, minWidth: 0 },
  fieldLabel: { fontFamily: "monospace", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8a939e", marginTop: 8 },
  input: { background: "#0f1216", color: "#e6eaef", border: "1px solid #262c34", borderRadius: 8, padding: "10px 12px", fontSize: 16 },
  smallArea: { height: 72, resize: "vertical", background: "#0f1216", color: "#dfe4ea", border: "1px solid #262c34", borderRadius: 8, padding: 10, fontSize: 16, lineHeight: 1.45 },
  editBtns: { display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" },
  miniBtn: { background: "#171b20", color: "#c8cfd7", border: "1px solid #262c34", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 14, minHeight: 40 },
  stripWrap: { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 6 },
  beatCard: { flex: "0 0 auto", width: 150, textAlign: "left", display: "flex", flexDirection: "column", gap: 4, background: "#111418", color: "#c8cfd7", border: "1px solid #262c34", borderRadius: 10, padding: "8px 12px", cursor: "pointer" },
  beatCardOn: { borderColor: "#ff5d33", background: "#171b20", boxShadow: "0 0 0 2px rgba(255,93,51,0.25)" },
  beatRole: { fontFamily: "monospace", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#ff8a66" },
  beatTitle: { fontSize: 12.5, lineHeight: 1.3, color: "#e6eaef", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" },
  beatDur: { fontFamily: "monospace", fontSize: 11, color: "#8a939e" },
};
